const destinations = [
  {
    name: "Mexico",
    context: "Manufacturing supplier visits, agricultural export meetings in Monterrey and Guadalajara, family vacation travel to Cancun and Los Cabos",
    urgency: "Business travel to Mexico often booked 1–2 weeks out when deals close",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Agricultural trade shows, livestock purchasing in Saskatchewan and Alberta, family trips to Vancouver and Toronto",
    urgency: "Cattle buying seasons create spring and fall surges in last-minute passport needs",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Germany & Europe",
    context: "MSSU study-abroad programs in Spain and Germany, manufacturing industry conferences in Frankfurt and Munich",
    urgency: "Study-abroad deadlines hit October and February — many students discover passport needs after acceptance",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Caribbean & Bahamas",
    context: "Family vacation cruises departing from Galveston and New Orleans, honeymoon travel, spring break trips",
    urgency: "Last-minute cruise deal bookings in the Tri-State area create 2–4 week passport windows",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom",
    context: "Healthcare conference travel, Route 66 history enthusiasts connecting to UK, executive business meetings",
    urgency: "Conference registration months out but passport check often skipped until flight booking",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Costa Rica & Central America",
    context: "MSSU international programs, eco-tourism family trips, medical mission travel from Joplin faith communities",
    urgency: "Mission trip and medical program travel has fixed departure windows that cannot be delayed",
    icon: "ri-map-pin-2-line",
  },
];

const commonSituations = [
  {
    stat: "3+ Hours",
    label: "drive to the nearest Regional Passport Agency in St. Louis — eliminated by using Fast Passport Center",
  },
  {
    stat: "55%",
    label: "of Joplin-area expedited requests involve Mexico or Canada cross-border business travel",
  },
  {
    stat: "2–4 Weeks",
    label: "average notice Tri-State travelers have before a planned international business trip or family vacation",
  },
  {
    stat: "Spring & Fall",
    label: "peak seasons when agricultural trade travel, study-abroad deadlines, and family vacation bookings collide",
  },
];

export default function JoplinDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Joplin &amp; Tri-State Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Joplin-area clients. Understanding local travel behavior is part of why our document review catches issues before they derail your business trip, family vacation, or study-abroad program.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Missouri Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Missouri with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/springfield-mo" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Springfield <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/kansas-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Kansas City <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/st-louis" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                St. Louis <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}